import React, { useEffect, useState } from 'react';
import { Router } from 'react-router';
import { useSelector } from 'react-redux';
import { Route, Redirect, Switch } from 'react-router-dom';

import { Login } from 'pages/Login';
import { NotAuthorized } from 'pages/NotAuthorized';
import { ConfirmAccount } from 'pages/ConfirmAccount';
import { ResetPassword } from 'pages/ResetPassword';
import { ForgotPassword } from 'pages/ForgotPassword';
import { Stacks } from 'pages/Stacks';
import { Stack } from 'pages/Stack';
import { Organizations } from 'pages/Organizations';
import { Organization } from 'pages/Organization';
import { Department } from 'pages/Department';
import { Dashboard } from 'pages/Dashboard';
import { Page404 } from 'pages/Page404';
import { LoginLayout } from 'layouts/LoginLayout';
import { ApplicationState } from 'store';
import { AuthRoute } from 'shared/AuthRoute';
import { history } from 'helpers/history';
import { SawmillStacks } from 'pages/SawmillStacks';

export enum NonAuthRoutes {
  Login = '/',
  Notauthorized = '/notauthorized',
  ConfirmAccount = '/confirm',
  ForgotPassword = '/forgot-password',
  ResetPassword = '/reset-password',
}

export enum AuthRoutes {
  Organizations = '/organizations',
  Dashboard = '/organization/:orgId/dashboard',
  DashboardWithDepartment = '/organization/:orgId/department/:deptId/dashboard',
  Stacks = '/organization/:orgId/department/:deptId/stacks',
  Organization = '/organization/:orgId',
  Stack = '/organization/:orgId/department/:deptId/stack/:stackId',
  Department = '/organization/:orgId/department/:deptId',
  SawmillStacks = '/sawmill/stacks',
}

const Routes: React.FC = (): JSX.Element => {
  const [startRoute, setStartRoute] = useState<string>('/');
  const isLoggedIn = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.isLoggedIn,
  );

  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess,
  );

  useEffect(() => {
    if (isLoggedIn) {
      if (!userAccess) {
        setStartRoute(NonAuthRoutes.Notauthorized);
        return;
      }
      if (userAccess?.isGlobalAdmin) {
        setStartRoute(AuthRoutes.Organizations);
        return;
      }
      if (
        userAccess?.organizations?.length === 0 &&
        userAccess?.departments?.length === 0 &&
        userAccess?.sawmills?.length === 0
      ) {
        setStartRoute(NonAuthRoutes.Notauthorized);
        return;
      }

      if (userAccess?.organizations && userAccess.organizations.length > 1) {
        setStartRoute(AuthRoutes.Organizations);
        return;
      }

      if (userAccess?.organizations?.length === 1) {
        setStartRoute(
          `/organization/${userAccess?.organizations[0].id}/dashboard`,
        );
        return;
      }
      if (
        userAccess?.organizations?.length === 0 &&
        userAccess?.departments?.length !== 0
      ) {
        if (userAccess?.departments) {
          setStartRoute(
            `/organization/${userAccess?.departments[0]?.organizationId}/department/${userAccess?.departments[0]?.id}/dashboard`,
          );
        }
      }

      if (userAccess?.sawmills?.length > 0) {
        setStartRoute(AuthRoutes.SawmillStacks);
      }
    }
  }, [
    userAccess,
    userAccess?.departments,
    userAccess?.organizations,
    userAccess?.sawmills,
    isLoggedIn,
  ]);

  return (
    <Router history={history}>
      <Switch>
        <Route
          exact
          path="/"
          render={() =>
            !isLoggedIn ? (
              <LoginLayout>
                <Login />
              </LoginLayout>
            ) : (
              <Redirect to={startRoute} />
            )
          }
        />
        <Route
          path={NonAuthRoutes.Notauthorized}
          render={() => (
            <LoginLayout>
              <NotAuthorized />
            </LoginLayout>
          )}
        />
        <Route
          path={NonAuthRoutes.ConfirmAccount}
          render={() => (
            <LoginLayout>
              <ConfirmAccount />
            </LoginLayout>
          )}
        />
        <Route
          path={NonAuthRoutes.ForgotPassword}
          render={() => (
            <LoginLayout>
              <ForgotPassword />
            </LoginLayout>
          )}
        />
        <Route
          path={NonAuthRoutes.ResetPassword}
          render={() => (
            <LoginLayout>
              <ResetPassword />
            </LoginLayout>
          )}
        />
        <AuthRoute
          exact
          path={AuthRoutes.Organizations}
          Component={Organizations}
        />
        <AuthRoute exact path={AuthRoutes.Dashboard} Component={Dashboard} />
        <AuthRoute
          exact
          path={AuthRoutes.DashboardWithDepartment}
          Component={Dashboard}
        />
        <AuthRoute exact path={AuthRoutes.Stacks} Component={Stacks} />
        <AuthRoute exact path={AuthRoutes.Stack} Component={Stack} />
        <AuthRoute
          exact
          path={AuthRoutes.Organization}
          Component={Organization}
        />
        <AuthRoute exact path={AuthRoutes.Department} Component={Department} />
        <AuthRoute
          exact
          path={AuthRoutes.SawmillStacks}
          Component={SawmillStacks}
        />
        <Route component={Page404} />
      </Switch>
    </Router>
  );
};

export default Routes;
